import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useEffectOnce } from 'react-use';
import { OFFER_STEPS } from '../constants/steps';
import { useDefaultBuyer } from '../hooks/useDefaultBuyer';
import {
  CREATE_OFFER_CONTEXT_DEFAULTS,
  CreateOfferContext,
  type CreateOfferContextType,
} from './CreateOfferContext';

interface Props {
  children: ReactNode;
  sourceId?: string;
  buyerId?: string;
  openedFromListing?: boolean;
}

export const CreateOfferProvider = ({
  children,
  sourceId,
  buyerId,
  openedFromListing,
}: Props) => {
  const formContainerRef = useRef<HTMLDivElement>(null);

  const { defaultBuyer, defaultBuyerLoading, error } = useDefaultBuyer({
    sourceId,
    buyerId,
  });

  const [contextValue, setContextValue] = useState<CreateOfferContextType>({
    ...CREATE_OFFER_CONTEXT_DEFAULTS,
    step: openedFromListing
      ? OFFER_STEPS.buyer
      : CREATE_OFFER_CONTEXT_DEFAULTS.step,
    openedFromListing,
    linkSource: openedFromListing ? 'lom' : undefined,
    sourceId,
    buyerId,
    formContainerRef,
    defaultBuyer,
    defaultBuyerLoading,
  });

  const updateContext = useCallback(
    (newContext: Partial<CreateOfferContextType>) => {
      setContextValue((current) => ({
        ...current,
        ...newContext,
      }));
    },
    [setContextValue],
  );

  // Update context when default buyer data changes
  useEffect(() => {
    setContextValue((current) => ({
      ...current,
      defaultBuyer,
      defaultBuyerLoading,
      errorMessage: error,
    }));
  }, [defaultBuyer, defaultBuyerLoading, error]);

  // Reset scroll after the new step renders — a synchronous smooth scrollTo
  // is dropped by iOS Safari/WKWebView when fired in the same tick as the
  // step change, before the new step is laid out
  const { step } = contextValue;
  useEffect(() => {
    if (formContainerRef.current) {
      formContainerRef.current.scrollTop = 0;
    }
  }, [step]);

  useEffect(() => {
    // Handle browser back/forward button
    const handlePopState = () => {
      const { pathname } = window.location;
      let step = OFFER_STEPS.address;
      if (pathname.includes('buyers')) {
        step = OFFER_STEPS.buyer;
      }
      updateContext({ step });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [updateContext]);

  useEffectOnce(() => {
    updateContext({ updateContext });
  });

  return (
    <CreateOfferContext.Provider value={contextValue}>
      {children}
    </CreateOfferContext.Provider>
  );
};
